import { Injectable } from "@angular/core";
import { App } from "@capacitor/app";
import * as semver from "semver";
import { environment } from "@environments/environment";
import { AppVersionsApiService } from "./app-versions-api.service";
import { isIos, isNative } from "../utils/runtime.utils";

export type AppUpdateState = "force" | "recommend" | "none";

export const appUpdateStateOverrideKey = "appUpdateStateOverride";

@Injectable({
  providedIn: "root",
})
export class AppUpdateService {
  constructor(private readonly appVersionsApiService: AppVersionsApiService) {}

  public async getInstalledVersion(): Promise<string> {
    if (!isNative()) {
      return environment.appVersion;
    }

    const info = await App.getInfo();
    return info.version;
  }

  public async getUpdateState(): Promise<AppUpdateState> {
    const overriddenState = this.getTestUpdateStateOverride();
    if (overriddenState !== null) {
      return overriddenState;
    }

    if (!isNative()) {
      return "none";
    }

    const config = await this.appVersionsApiService.getAppVersions();
    const platformConfig = isIos() ? config.ios : config.android;
    const installedVersion = await this.getInstalledVersion();

    if (!semver.valid(installedVersion)) {
      return "none";
    }

    if (
      semver.valid(platformConfig.minimumSupportedVersion) &&
      semver.lt(installedVersion, platformConfig.minimumSupportedVersion)
    ) {
      return "force";
    }

    if (
      platformConfig.latestVersion &&
      semver.valid(platformConfig.latestVersion) &&
      semver.gt(platformConfig.latestVersion, installedVersion)
    ) {
      return "recommend";
    }

    return "none";
  }

  public openStore(): void {
    window.location.href = isIos() ? environment.appleStoreUrl : environment.androidStoreUrl;
  }

  private getTestUpdateStateOverride(): AppUpdateState | null {
    if (environment.production) {
      return null;
    }

    const overriddenState = localStorage.getItem(appUpdateStateOverrideKey);
    if (overriddenState === "force" || overriddenState === "recommend" || overriddenState === "none") {
      return overriddenState;
    }

    return null;
  }
}
